// One project (feature "projects"; plan phase 2, chat in a project). Two
// routes share one frame (design review ui.region_gate project_page:
// home_layout two_column_wide, section_container rows, conv_files
// right_column_when_wide_else_disclosure):
//   /project/:id          the project's home: a quiet row with when it was
//                         made and the moreH Menu (Ganti nama, Hapus: the
//                         same dialogs as the sidebar row, shell/Projects.tsx),
//                         then the chat (ChatScreen scoped to the project:
//                         its empty heading, the page's h1, is the project's
//                         name with the composer centred under it; the first
//                         turn moves to the conversation route)
//   /project/:id/c/:conv  one conversation in the project: Kembali ke
//                         <project>, then the thread
// On every home (folder, paket, PBJ) the chat's composer renders in a dock
// pinned to the bottom of the chat column (.pj-dock, ChatScreen's `dock`), so
// it stays in sight while the page above it scrolls.
// Beside the chat, a column with Chat di project ini (project/ProjectChats)
// and File project (project/ProjectFiles). The frame is a container: from a
// 960px wide main the column sits on the right and scrolls on its own; below
// that the home stacks everything in one scroller and the conversation
// keeps the thread full height, with File project behind a toggle under the
// back link.
// A Penyedia paket project (kind "paket", POC) has its own home
// (project/Paket.tsx: the paket header, the stage stepper, the checklist,
// Pendampingan, then the same chat and columns); its conversations use the
// conversation route below unchanged.
// A Non-Penyedia PBJ project (kind "pbj", migration 0023) has its own home
// too (pbj/PbjProject.tsx: the route, the six stages and their activities,
// then the same chat and columns); its conversations use the route below.
// The project comes from the shell's shared list (GET /projects); there is
// no single-project endpoint. An id that is not in the list (unknown, or
// just deleted) is Halaman tidak ditemukan.
import { useEffect, useState, type ReactNode } from "react";
import * as API from "../api";
import { formatDateLong } from "../lib/format";
import { ProjectChats } from "../project/ProjectChats";
import { ProjectFilesPanel } from "../project/ProjectFiles";
import { PaketProject, firstName } from "../project/Paket";
import { PbjProject } from "../pbj/PbjProject";
import { useUser } from "../auth";
import { PaketProvider } from "../project/PaketContext";
import { PANEL_ID, PanelHead, PanelToggle, useSidePanel } from "../project/SidePanel";
import { StageStepper } from "../project/PaketParts";
import { paketTitle } from "../project/stages";
import { chipsForStage, projectDraftExample } from "../project/stage-chips";
import { projectPath, useProjects } from "../shell/Projects";
import { Notice, SkeletonRows, useTitle } from "../ui/components";
import { Glyph } from "../ui/icons";
import { Menu } from "../ui/Menu";
import { ChatScreen } from "./Chat";
import { NotFoundScreen } from "./Status";

/** The route fills main; every state scrolls inside it. */
function Scroll({ children }: { children: ReactNode }) {
  return (
    <div className="pj-scroll" data-ui-scroller="">
      <div className="page">{children}</div>
    </div>
  );
}

export interface ProjectScreenProps {
  id: string;
  /** /project/:id/c/:conversationId, or the project's home without it. */
  conversationId?: string;
}

export function ProjectScreen({ id, conversationId }: ProjectScreenProps) {
  const projects = useProjects();
  const list = projects?.list;
  const project = list?.data?.find((p) => p.id === id) ?? null;
  const [filesOpen, setFilesOpen] = useState(false);
  const [panelOpen] = useSidePanel();
  const [dock, setDock] = useState<HTMLDivElement | null>(null);
  const user = useUser();
  const panel = panelOpen ? "open" : "closed";
  useTitle(project?.name ?? "Project");

  // Another conversation starts with the files folded again.
  useEffect(() => setFilesOpen(false), [id, conversationId]);

  if (!projects || !list) {
    return (
      <Scroll>
        <NotFoundScreen />
      </Scroll>
    );
  }

  if (list.loading && !list.data) {
    return (
      <Scroll>
        <SkeletonRows count={2} label="Memuat project" />
      </Scroll>
    );
  }

  if (list.error && !list.data) {
    return (
      <Scroll>
        <Notice
          tone="danger"
          title="Project belum termuat"
          action={
            <button type="button" className="btn btn-secondary" onClick={list.reload}>
              Coba lagi
            </button>
          }
        >
          <p>{API.errorMessage(list.error)}</p>
        </Notice>
      </Scroll>
    );
  }

  if (!project) {
    return (
      <Scroll>
        <NotFoundScreen />
      </Scroll>
    );
  }

  const isPaket = project.kind === "paket";

  if (conversationId) {
    const view = (
      <div className="pj" data-view="conversation" data-panel={panel} data-files-open={filesOpen ? "" : undefined}>
        <div className="pj-layout">
          <div className="pj-bar">
            <a className="pj-back" href={projectPath(project.id)} title={`Kembali ke ${project.name}`}>
              <Glyph name="arrowLeft" size={18} />
              <span className="pj-back-text">Kembali ke {project.name}</span>
            </a>
            <span className="pj-bar-end">
              {/* Narrow: File project unfolds the files; wide: the panel toggle. */}
              <button type="button" className="btn btn-secondary pj-files-toggle" aria-expanded={filesOpen} aria-controls={PANEL_ID} onClick={() => setFilesOpen((o) => !o)}>
                <Glyph name="folder" size={18} />
                <span>File project</span>
              </button>
              <PanelToggle className="pj-panel-toggle-wide" />
            </span>
            {isPaket ? (
              // The paket's stage stays in sight inside its conversations (compact stepper).
              <div className="pk-convbar">
                <p className="pk-convbar-title" title={paketTitle(project)}>
                  <Glyph name="box" size={16} />
                  <span>{paketTitle(project)}</span>
                </p>
                <StageStepper stage={project.stage ?? "persiapan_pemilihan"} compact />
              </div>
            ) : null}
          </div>
          <div className="pj-chat">
            <ChatScreen
              projectId={project.id}
              projectName={project.name}
              conversationId={conversationId}
              suggestions={isPaket ? chipsForStage(project.stage) : undefined}
              draftExample={projectDraftExample(user.role, project.name)}
            />
          </div>
          <aside id={PANEL_ID} className="pj-aside" aria-label="Isi project" data-ui-scroller="">
            <PanelHead />
            <ProjectFilesPanel projectId={project.id} />
            <ProjectChats projectId={project.id} currentId={conversationId} />
          </aside>
        </div>
      </div>
    );
    return isPaket ? (
      <PaketProvider project={project} projects={projects}>
        {view}
      </PaketProvider>
    ) : (
      view
    );
  }

  if (project.kind === "pbj") return <PbjProject project={project} projects={projects} />;

  if (isPaket) {
    return (
      <PaketProvider project={project} projects={projects}>
        <PaketProject project={project} projects={projects} />
      </PaketProvider>
    );
  }

  return (
    <div className="pj" data-view="home" data-panel={panel}>
      <div className="pj-layout" data-ui-scroller="">
        <header className="pj-head">
          <div className="pj-head-row">
            <p className="pj-meta">
              <Glyph name="folder" size={18} />
              <span>Dibuat {formatDateLong(project.createdAt)}</span>
            </p>
            <span className="pj-head-actions">
              <PanelToggle />
              <Menu
              label={`Tindakan untuk ${project.name}`}
              align="end"
              className="pj-menu"
              items={[
                { id: "rename", label: "Ganti nama", icon: "edit2", onSelect: () => projects.openRename(project) },
                { id: "delete", label: "Hapus", icon: "trash", tone: "danger", onSelect: () => projects.openDelete(project) },
              ]}
              />
            </span>
          </div>
        </header>
        <div className="pj-chat">
          <ChatScreen
            projectId={project.id}
            projectName={project.name}
            greetingMessage={{ text: `Halo ${firstName(user.name)}, ini project ${project.name}. Tanyakan apa saja atau unggah dokumen untuk mulai.`, keepHeading: true }}
            draftExample={projectDraftExample(user.role, project.name)}
            dock={dock}
          />
        </div>
        <aside id={PANEL_ID} className="pj-aside" aria-label="Isi project" data-ui-scroller="">
          <PanelHead />
          <ProjectChats projectId={project.id} />
          <ProjectFilesPanel projectId={project.id} />
        </aside>
        <div ref={setDock} className="pj-dock no-print" />
      </div>
    </div>
  );
}
